import React, { useCallback } from 'react'
import { FileWithPath } from 'react-dropzone'

import { SupportedUploadPair } from '../../graphql/uploadPerEntity'
import DropBox from './DropBox'
import ImageBox, { FileStatus, ImageProps } from './ImageBox'
import ImageBoxSkeleton from './ImageBox.skeleton'

// entityType/imageType arrive as a correlated pair, not two free-standing props — see
// SupportedUploadPair. An entity with no upload mutation (Booking, WBSSite) no longer
// compiles here.
type UploadBoxProps = SupportedUploadPair & {
  instantUpload?: boolean
  entityid: string
  entityCode?: string
  images: ImageProps[]
  setImages: React.Dispatch<React.SetStateAction<ImageProps[]>>
  loading: boolean
  hasAccess: boolean | undefined
  roomTypeCode?: string
}

export const UploadBox: React.FC<UploadBoxProps> = (props) => {
  const {
    instantUpload = true,
    entityid,
    entityCode,
    images,
    setImages,
    imageType,
    loading,
    hasAccess = false,
    roomTypeCode
  } = props

  // Kept as one object rather than two destructured variables. Destructuring a correlated
  // union splits it into two independently-widened variables, and TypeScript can then no
  // longer prove the pair passed down is one of the eight — spreading the object preserves
  // the correlation all the way to ImageBox.
  const uploadPair: SupportedUploadPair = props

  const onDrop = useCallback((acceptedFiles: FileWithPath[]) => {
    if (imageType === 'Logo' || imageType === 'Cover') {
      setImages(() => [
        ...acceptedFiles.map((singleFile: FileWithPath) => ({
          file: singleFile,
          status: instantUpload ? FileStatus.UPLOADING : FileStatus.TYPING_NAME,
          fileName: singleFile.name,
          fileURL: URL.createObjectURL(singleFile),
          fileSize: singleFile.size,
          mediaID: ''
        }))
      ])
    } else {
      setImages((currentImages) => [
        ...acceptedFiles.map((singleFile: FileWithPath) => ({
          file: singleFile,
          status: instantUpload ? FileStatus.UPLOADING : FileStatus.TYPING_NAME,
          fileName: singleFile.name,
          fileURL: URL.createObjectURL(singleFile),
          fileSize: singleFile.size,
          mediaID: ''
        })),
        ...currentImages
      ])
    }
  }, [])

  const handleChangeImage = useCallback((newImage: ImageProps) => {
    setImages((currentImages) => {
      const imageIndex = currentImages.findIndex(
        (imageItem) => imageItem.fileURL === newImage.fileURL
      )
      return [
        ...currentImages.slice(0, imageIndex),
        newImage,
        ...currentImages.slice(imageIndex + 1)
      ]
    })
  }, [])

  const handleDelete = useCallback((fileURL: string) => {
    setImages((currentImages) => [
      ...currentImages.filter((tmp) => tmp.fileURL !== fileURL)
    ])
  }, [])

  return (
    <div>
      {hasAccess && <DropBox onDrop={onDrop} />}
      {loading ? (
        <ImageBoxSkeleton />
      ) : (
        images.map((item) => {
          return item.fileURL ? (
            <ImageBox
              key={item.fileURL}
              item={item}
              onChange={handleChangeImage}
              onDelete={handleDelete}
              entityid={entityid}
              entityCode={entityCode}
              {...uploadPair}
              roomTypeCode={roomTypeCode}
            />
          ) : null
        })
      )}
    </div>
  )
}
